import React, { useState, useEffect, useCallback, useRef, useLayoutEffect } from 'react'

import Flag from 'react-world-flags'
import { formatAsYouType, getCountryCode } from 'utils/numberUtils'
import { ReactComponent as IconClear } from 'assets/svg/shared/remove.svg'
import { getUpdatedCursorPosition } from 'common/Helpers'
import { Loading } from 'components/shared'
import cn from 'classnames'

export default function PhoneInput({
    value,
    onChange = () => {},
    onTarget = () => {},
    onReset = () => {},
    inputHandlerPositionTop,
    helpClass = '',
    placeholder = '',
    loading = false
}) {
    const [isCountryFlagVisible, setIsCountryFlagVisible] = useState(false)
    const [flagCountryCode, setFlagCountry] = useState(null)

    const prevValue = useRef('')
    const prevCursorPosition = useRef(0)
    const inputRef = useRef(null)

    const typeFormatter = useCallback(
        (val) => {
            const countryCode = getCountryCode(val)

            if (countryCode) {
                setFlagCountry(countryCode)
                setIsCountryFlagVisible(true)
                val = formatAsYouType(val)
            } else {
                setIsCountryFlagVisible(false)
            }

            onChange(val)
        },
        [onChange]
    )

    useEffect(() => {
        typeFormatter(value)
    }, [value, typeFormatter])

    useLayoutEffect(() => {
        const inputElement = inputRef.current

        // sometimes prevValue.current is undefined. So we must reset it to its default value
        if (!prevValue.current) {
            prevValue.current = ''
        }

        const wasCursorAtEnd = prevCursorPosition.current === prevValue.current.length

        if (inputElement && !wasCursorAtEnd) {
            const updatedCursorPosition = getUpdatedCursorPosition(prevCursorPosition.current, prevValue.current, value)

            inputElement.selectionStart = updatedCursorPosition
            inputElement.selectionEnd = updatedCursorPosition
        }

        prevValue.current = value
    }, [value])

    // Avoid cursor jump to the end when fixing earlier mistype
    const handleKeyDown = useCallback(() => {
        const inputElement = inputRef.current
        const currentCursorPosition =
            inputElement.selectionStart === inputElement.selectionEnd
                ? inputElement.selectionStart
                : inputElement.selectionEnd
        prevCursorPosition.current = currentCursorPosition
    }, [])

    const handleChange = (event) => {
        typeFormatter(event.target.value.trimLeft())
        onTarget('input')
    }

    //dialpad__input-holder dialpad__input-holder-sticky
    return (
        <div
            className={cn(
                `dialpad__input-holder dialpad__input-holder-sticky`,
                inputHandlerPositionTop && 'dialpad__input-holder-sticky-top',
                helpClass && `${helpClass}`
            )}
        >
            {flagCountryCode && isCountryFlagVisible && (
                <Flag className="dialpad__input-flag" code={flagCountryCode.toLowerCase()} height="20" />
            )}
            {loading && <Loading />}
            <input
                type="text"
                className="dialpad__input ltr"
                value={value}
                onChange={handleChange}
                onKeyDown={handleKeyDown}
                placeholder={placeholder}
                ref={inputRef}
            />
            {value && (
                <button className="dialpad__btn-clear" onClick={onReset}>
                    <IconClear />
                </button>
            )}
        </div>
    )
}
